/**
 * UK postcode formatting and validation, mirroring PostcodeNormalizer on the backend.
 *
 * The client validates for immediate feedback only; the API validates again and is the authority.
 */

const UK_POSTCODE =
  /^(GIR ?0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y])?)|[0-9][A-HJKPS-UW]) ?[0-9][ABD-HJLNP-UW-Z]{2})$/;

/** Strips everything that cannot appear in a postcode and uppercases the rest. */
export function compactPostcode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Formats as the tenant types.
 *
 * Inserts the single space before the inward code once there are enough characters for one, and
 * never reformats a partial entry in a way that fights the caret.
 */
export function autoFormatPostcode(raw: string): string {
  const compact = compactPostcode(raw).slice(0, 7);
  if (compact.length <= 4) {
    return compact;
  }
  return `${compact.slice(0, compact.length - 3)} ${compact.slice(compact.length - 3)}`;
}

/** Canonical ONS form, or null when the value is not a UK postcode. */
export function normalisePostcode(raw: string): string | null {
  const compact = compactPostcode(raw);
  if (compact.length < 5 || compact.length > 7) {
    return null;
  }
  const candidate = `${compact.slice(0, compact.length - 3)} ${compact.slice(compact.length - 3)}`;
  return UK_POSTCODE.test(candidate) ? candidate : null;
}

export function isValidPostcode(raw: string): boolean {
  return normalisePostcode(raw) !== null;
}
